import { LoginProxy } from './../http/login-proxy.service';
import { loginRoute } from './../../core/routing/route-names';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { filter, switchMap, take, catchError, map } from 'rxjs/operators';
import { SpinnerService } from '../spinner/spinner.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private router: Router,
    private spinnerService: SpinnerService,
    private loginProxy: LoginProxy
  ){}

  intercept(originalRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const request = this.addAuthorizationHeaderIfPossible(originalRequest);
    if (request.headers.get('Authorization') == null) {
      return next.handle(request);
    }

    return next.handle(request).pipe(catchError((err: any) => {
      if (err instanceof HttpErrorResponse) {
        if (err.status !== 401 && err.status !== 403) {
          return throwError(() => err);
        }

        if (this.refreshTokenInProgress) {
          return this.refreshTokenSubject
            .pipe(
              filter(result => result !== null),
              take(1),
              switchMap(() => {
                const newRequest = this.addAuthorizationHeaderIfPossible(request);
                return next.handle(newRequest)
              })
            );
        }

        this.refreshTokenInProgress = true;
        this.refreshTokenSubject.next(null);
        return this.refreshTokenLogin(err).pipe(
          switchMap(() => {
            this.refreshTokenInProgress = false;
            this.refreshTokenSubject.next(true);
            const newRequest = this.addAuthorizationHeaderIfPossible(request);
            return next.handle(newRequest);
          }),
          catchError((err: any) => {
            this.refreshTokenInProgress = false;
            this.spinnerService.showSpinner = false;
            localStorage.removeItem(environment.localStorageToken);
            localStorage.removeItem(environment.localStorageRefreshToken);
            this.router.navigate([loginRoute]);
            return of(null);
          })
        );
      }
      throw err;
    }));
  }

  private addAuthorizationHeaderIfPossible(request: HttpRequest<any>): HttpRequest<any> {
    if (!request.url.includes(environment.apiServerUrl)) {
      return request;
    }

    const token = localStorage[environment.localStorageToken];
    if (!token) {
      return request;
    }

    return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
  }

  private refreshTokenLogin(originalError: any): Observable<void>  {
    const refreshToken = localStorage[environment.localStorageRefreshToken];
    if (!refreshToken) {
      throw originalError;
    }
    return this.loginProxy.refreshTokenLogin(localStorage[environment.localStorageRefreshToken]).pipe(
      map(([token, refreshToken]) => {
        // login successful if there's a jwt token in the response
        if (token) {
          localStorage[environment.localStorageToken] = token;
        }
        if (refreshToken) {
          localStorage[environment.localStorageRefreshToken] = refreshToken;
        }
      })
    );
  }
}
